import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Search, FolderKanban, Image, MessageSquare, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { StaggerList, StaggerItem } from '../../components/animations/StaggerList';
import { api } from '../../lib/api';

interface Project {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string;
  description: string | null;
  coverImageUrl: string | null;
  status: string;
  workItemCount: number;
  pendingApprovalCount: number;
  commentCount: number;
  createdAt: string;
}

export function ProjectsPage() {
  const [search, setSearch] = useState('');
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | null>(null);

  useEffect(() => {
    async function fetchProjects() {
      try {
        setIsLoading(true);
        const queryParams = statusFilter ? `?status=${statusFilter}` : '';
        const response = await api.get<Project[]>(`/projects${queryParams}`);
        setProjects(Array.isArray(response.data) ? response.data : []);
        setError(null);
      } catch (err) {
        setError('Failed to load projects');
        console.error(err);
      } finally {
        setIsLoading(false);
      }
    }
    fetchProjects();
  }, [statusFilter]);

  const filteredProjects = projects.filter(
    (project) =>
      project.name.toLowerCase().includes(search.toLowerCase()) ||
      project.organizationName?.toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-3xl font-bold">Projects</h1>
          <p className="text-text-secondary mt-1">
            Manage and upload work for your clients
          </p>
        </div>
        <Link to="/projects/new">
          <Button>
            <Plus className="w-4 h-4" />
            New Project
          </Button>
        </Link>
      </motion.div>

      {/* Search & Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex flex-col sm:flex-row gap-4"
      >
        <Input
          placeholder="Search projects..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          icon={<Search className="w-4 h-4" />}
          className="max-w-md"
        />
        <div className="flex gap-2">
          <Button
            variant={statusFilter === null ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter(null)}
          >
            All
          </Button>
          <Button
            variant={statusFilter === 'active' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('active')}
          >
            Active
          </Button>
          <Button
            variant={statusFilter === 'completed' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setStatusFilter('completed')}
          >
            Completed
          </Button>
        </div>
      </motion.div>

      {/* Projects Grid */}
      <StaggerList className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredProjects.map((project) => (
          <StaggerItem key={project.id}>
            <Link to={`/projects/${project.id}`}>
              <Card className="group cursor-pointer overflow-hidden" glow>
                {/* Cover Image */}
                <div className="relative h-40 overflow-hidden bg-bg-tertiary">
                  {project.coverImageUrl ? (
                    <motion.img
                      src={project.coverImageUrl}
                      alt={project.name}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FolderKanban className="w-16 h-16 text-text-muted opacity-50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <Badge
                      variant={project.status === 'active' ? 'success' : 'default'}
                      className="mb-2"
                    >
                      {project.status}
                    </Badge>
                    <h3 className="text-white font-semibold text-lg group-hover:text-accent-light transition-colors">
                      {project.name}
                    </h3>
                    <p className="text-white/70 text-sm">{project.organizationName}</p>
                  </div>
                </div>

                {/* Stats */}
                <CardContent className="p-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-4">
                      <span className="flex items-center gap-1.5 text-text-secondary">
                        <Image className="w-4 h-4" />
                        {project.workItemCount}
                      </span>
                      <span className="flex items-center gap-1.5 text-text-secondary">
                        <MessageSquare className="w-4 h-4" />
                        {project.commentCount}
                      </span>
                    </div>
                    {project.pendingApprovalCount > 0 ? (
                      <Badge variant="warning" animated>
                        {project.pendingApprovalCount} pending
                      </Badge>
                    ) : (
                      <span className="flex items-center gap-1 text-green-500">
                        <CheckCircle2 className="w-4 h-4" />
                        All approved
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          </StaggerItem>
        ))}
      </StaggerList>

      {/* Empty state */}
      {filteredProjects.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <FolderKanban className="w-12 h-12 mx-auto text-text-muted mb-4" />
          <h3 className="text-lg font-semibold mb-2">No projects found</h3>
          <p className="text-text-secondary mb-4">
            {search
              ? 'Try adjusting your search'
              : 'Get started by creating your first project'}
          </p>
          {!search && (
            <Link to="/projects/new">
              <Button>
                <Plus className="w-4 h-4" />
                Create Project
              </Button>
            </Link>
          )}
        </motion.div>
      )}
    </div>
  );
}
